/* ============================================================
   MASTY — predictive search
   Opens the search modal, queries Shopify /search/suggest.json,
   renders product cards into the modal. Self-contained IIFE.
   Reads config from window.MastyCartConfig; money formatting
   via window.MastyCart.formatMoney when a numeric price is seen.
   ============================================================ */
(function () {
  'use strict';

  var CONFIG = window.MastyCartConfig || {};
  var strings = CONFIG.strings || {};
  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 250;
  var LIMIT = 8;

  var modal, input, resultsEl, lastFocus, debounceTimer, activeController;

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Shopify suggest.json returns product.price as a formatted string in most
  // themes; if a raw number (cents) comes through, format via the cart engine.
  function formatPrice(price) {
    if (price == null || price === '') { return ''; }
    if (typeof price === 'number') {
      if (window.MastyCart && typeof window.MastyCart.formatMoney === 'function') {
        return window.MastyCart.formatMoney(price);
      }
      return (price / 100).toFixed(2);
    }
    // numeric-looking string of cents (e.g. "2900") — treat as cents
    var s = String(price);
    if (/^\d+$/.test(s) && window.MastyCart && typeof window.MastyCart.formatMoney === 'function') {
      return window.MastyCart.formatMoney(parseInt(s, 10));
    }
    return s; // already-formatted money string, e.g. "$29.00"
  }

  function sizedImage(url) {
    if (!url) { return ''; }
    return url.replace(/(\.[a-z]+)(\?|$)/i, '_360x$1$2');
  }

  function cardHTML(product) {
    var href = product.url || '#';
    var title = escapeHtml(product.title || '');
    var priceText = escapeHtml(formatPrice(product.price));
    var img = product.featured_image
      ? '<img src="' + escapeHtml(sizedImage(product.featured_image)) + '" alt="' + title + '" loading="lazy" width="360" height="450">'
      : '';
    return '' +
      '<a class="ps-card" href="' + escapeHtml(href) + '">' +
        '<div class="ps-card__media">' + img + '</div>' +
        '<div class="ps-card__title">' + title + '</div>' +
        (priceText ? '<div class="ps-card__price">' + priceText + '</div>' : '') +
      '</a>';
  }

  function renderResults(products, query) {
    if (!resultsEl) { return; }
    if (!products || !products.length) {
      var q = escapeHtml(query);
      var tmpl = strings.searchEmpty || 'No results for "[query]"';
      resultsEl.innerHTML =
        '<p class="search-modal__empty">' + tmpl.replace('[query]', q) + '</p>';
      return;
    }
    var cards = products.map(cardHTML).join('');
    resultsEl.innerHTML = '<div class="ps-grid">' + cards + '</div>';
  }

  function clearResults() {
    if (resultsEl) { resultsEl.innerHTML = ''; }
  }

  function search(query) {
    if (!resultsEl) { return; }

    // Abort any in-flight request (guard for older browsers).
    if (activeController && typeof activeController.abort === 'function') {
      try { activeController.abort(); } catch (e) {}
    }
    var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    activeController = controller;

    var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=product' +
      '&resources[limit]=' + LIMIT +
      '&resources[options][unavailable_products]=last';

    var opts = { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' };
    if (controller) { opts.signal = controller.signal; }

    fetch(url, opts)
      .then(function (r) {
        if (!r.ok) { throw new Error('search request failed'); }
        return r.json();
      })
      .then(function (data) {
        // Ignore stale responses if a newer request has started.
        if (activeController !== controller) { return; }
        var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        renderResults(products, query);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') { return; }
        if (activeController !== controller) { return; }
        renderResults([], query); // graceful: fall back to empty state
      });
  }

  function onInput() {
    var q = (input.value || '').trim();
    clearTimeout(debounceTimer);
    if (q.length < MIN_CHARS) {
      if (activeController && typeof activeController.abort === 'function') {
        try { activeController.abort(); } catch (e) {}
      }
      activeController = null;
      clearResults();
      return;
    }
    debounceTimer = setTimeout(function () { search(q); }, DEBOUNCE_MS);
  }

  function isOpen() {
    return modal && modal.classList.contains('is-open');
  }

  function open() {
    if (!modal || isOpen()) { return; }
    lastFocus = document.activeElement;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('search-open');
    if (input) {
      // Focus after the panel transition begins so it reliably takes.
      setTimeout(function () {
        try { input.focus(); input.select(); } catch (e) {}
      }, 60);
    }
  }

  function close() {
    if (!modal || !isOpen()) { return; }
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('search-open');
    clearTimeout(debounceTimer);
    if (activeController && typeof activeController.abort === 'function') {
      try { activeController.abort(); } catch (e) {}
    }
    activeController = null;
    if (lastFocus && typeof lastFocus.focus === 'function') {
      try { lastFocus.focus(); } catch (e) {}
    }
  }

  function onClick(e) {
    var toggle = e.target.closest('[data-search-toggle]');
    if (toggle) { e.preventDefault(); open(); return; }

    var closeBtn = e.target.closest('[data-search-close]');
    if (closeBtn) { e.preventDefault(); close(); return; }

    var overlay = e.target.closest('[data-search-overlay]');
    if (overlay) { e.preventDefault(); close(); return; }
  }

  function onKey(e) {
    if (e.key === 'Escape' && isOpen()) { close(); }
  }

  function init() {
    modal = $('[data-search-modal]');
    input = $('[data-search-input]');
    resultsEl = $('[data-search-results]');

    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    if (input) { input.addEventListener('input', onInput); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
